import * as Yup from "yup";
import React from "react";
import { Link } from 'react-router-dom';
import {
  TranslatedText, getCorrectLink,
} from "../../../helpers/helpers";

import { validEmail, validPhone } from "../../forms/CustomSignUp/validations";
import {
  CaseForms,
  CaseMutationType,
  CaseStepperType,
} from "../../../helpers/enums";
import getMenuConfig from "../../../helpers/menuConfig";
const { pdfLinks: { pdfModuloSepa } } = getMenuConfig();

// Il model definisce la struttura dei dati
// formId è un identificatore del form
// formField contiene la definizione dei singoli campi
const model = {
  formId: CaseForms.DomiciliazioneBancaria,
  formField: {
    campo_contract: {
      name: "campo_contract",
      label: <TranslatedText i18nKey="cases.contract-select.label" />,
      placeholder: <TranslatedText i18nKey="cases.contract-select.select-contract" />,
      loading: <TranslatedText i18nKey="cases.contract-select.loading" />,
      noContractFound: <TranslatedText i18nKey="cases.contract-select.no-contract-found" />,
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      type: "active-contract-select",
      excludeStatusTypes: ["inactive"],
      onSelected: (selected, values, setFieldValue, setFieldTouched) => {
        setFieldValue(comunePuf.name, selected.city);
        setFieldValue(integraClientCode.name, selected.clientCode);
        setFieldValue(
          indirizzoPuf.name,
          `${selected.street}, ${selected.streetNumber}`
        );
        setFieldTouched(comunePuf.name, false);
        setFieldTouched(integraClientCode.name, false);
        setFieldTouched(indirizzoPuf.name, false);
      },
    },
    comunePuf: {
      name: "comunePuf",
      label: <TranslatedText i18nKey="cases.request_info.fields.comune.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.required" />,
      type: "input",
      readonly: true,
    },
    indirizzoPuf: {
      name: "indirizzoPuf",
      label: <TranslatedText i18nKey="cases.request_info.fields.indirizzo.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.required" />,
      type: "input",
      readonly: true,
    },
    integraClientCode: {
      name: "integraClientCode",
      label: <TranslatedText i18nKey="cases.request_info.fields.codice_cliente.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      type: "input",
      readonly: true,
    },
    businessName: {
      name: "businessName",
      label: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.required" />,
      type: "input",
      readonly: true,
    },
    landlinePhoneNumber: {
      name: "landlinePhoneNumber",
      label: <TranslatedText i18nKey="cases.request_info.fields.telefono.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      invalidErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.telefono.invalid" />,
      type: "input",
      readonly: true,
    },
    mobilePhoneNumber: {
      name: "mobilePhoneNumber",
      label: <TranslatedText i18nKey="cases.request_info.fields.cellulare.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      invalidErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.cellulare.invalid" />,
      type: "input",
      readonly: true,
    },
    email: {
      name: "email",
      label: <TranslatedText i18nKey="cases.request_info.fields.email.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      invalidErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.email.invalid" />,
      type: "input",
      readonly: true,
    },
    pec: {
      name: "pec",
      label: <TranslatedText i18nKey="cases.request_info.fields.pec.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      invalidErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.pec.invalid" />,
      type: "input",
      readonly: true,
    },
    note: {
      name: "note",
      label: <TranslatedText i18nKey="cases.request_info.fields.nome_utente.label" />,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.placeholder" />,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.required" />,
      type: "text",
    },
  },
};

const {
  formField: {
    campo_contract,
    comunePuf,
    indirizzoPuf,
    integraClientCode,
    businessName,
    landlinePhoneNumber,
    mobilePhoneNumber,
    email,
    pec,
    note,
  },
} = model;

// initialValues contiene i valori iniziali del form, ne imposta anche i campi.
// Si può usare per popolare il form eventualmente.
const initialValues = {
  [campo_contract.name]: "",
  [comunePuf.name]: "",
  [indirizzoPuf.name]: "",
  [integraClientCode.name]: "",
  [businessName.name]: "",
  [landlinePhoneNumber.name]: "",
  [mobilePhoneNumber.name]: "",
  [email.name]: "",
  [pec.name]: "",
  [note.name]: "",
};

// Per definire il validationSchema si usa un array di shape che indicano gli step.
// L'ordine degli step deve corrispondere a quello in layout.
const getValidationSchema = () => {
  return [
    Yup.object().shape({
      [campo_contract.name]: Yup.string().required(comunePuf.requiredErrorMsg),
      [comunePuf.name]: Yup.string().required(comunePuf.requiredErrorMsg),
      [indirizzoPuf.name]: Yup.string().required(indirizzoPuf.requiredErrorMsg),
      [landlinePhoneNumber.name]: Yup.string().matches(validPhone, {
        message: landlinePhoneNumber.invalidErrorMsg,
      }),
      [mobilePhoneNumber.name]: Yup.string().matches(validPhone, {
        message: mobilePhoneNumber.invalidErrorMsg,
      }),
      [email.name]: Yup.string().matches(validEmail, {
        message: email.invalidErrorMsg,
      }),
      [pec.name]: Yup.string().matches(validEmail, {
        message: pec.invalidErrorMsg,
      }),
      [businessName.name]: Yup.string().required(businessName.requiredErrorMsg),
      [note.name]: Yup.string().required(note.requiredErrorMsg),
    }),
  ];
};

// Il layout contiene informazioni di testo e di distribuzione sulle griglie
const layout = [
  {
    chapter: true,
    chapterName: "Form semplice 1",
    title: "",
    fields: [
      {
        fieldName: null,
        text: (
          <h2 className="case-step-title">
            <TranslatedText i18nKey="cases.request_info.titles.title1" />
          </h2>
        ),
        grid: {
          xs: 12,
        },
      },
      {
        fieldName: "campo_contract",
        grid: {
          xs: 12,
          md: 8,
        },
      },
      {
        fieldName: comunePuf.name,
        grid: {
          xs: 12,
          md: 6,
        },
      },
      {
        fieldName: indirizzoPuf.name,
        grid: {
          xs: 12,
          md: 6,
        },
      },
      {
        fieldName: integraClientCode.name,
        grid: {
          xs: 12,
        },
      },
      {
        fieldName: null,
        text: (
          <h2 className="case-step-title">
            <TranslatedText i18nKey="cases.request_info.titles.title2" />
          </h2>
        ),
        grid: {
          xs: 12,
        },
      },
      {
        fieldName: businessName.name,
        grid: {
          xs: 12,
        },
      },
      {
        fieldName: landlinePhoneNumber.name,
        grid: {
          xs: 12,
          md: 6,
        },
      },
      {
        fieldName: mobilePhoneNumber.name,
        grid: {
          xs: 12,
          md: 6,
        },
      },
      {
        fieldName: email.name,
        grid: {
          xs: 12,
          md: 6,
        },
      },
      {
        fieldName: pec.name,
        grid: {
          xs: 12,
          md: 6,
        },
      },
      {
        fieldName: null,
        text: (
          <div className="info-label">
            <span className="gc-icon-functional-attention-2-col">
              <span className="path1"></span>
              <span className="path2"></span>
            </span>
            <p>
              {<TranslatedText i18nKey="cases.forms.user_profile.part1" />}
              {" "}
              <Link to="/user-profile">
                <u>
                  {<TranslatedText i18nKey="cases.forms.user_profile.part2" />}
                </u>
              </Link>
              {" "}
              {<TranslatedText i18nKey="cases.forms.user_profile.part3" />}
            </p>
          </div>
        ),
        grid: {
          xs: 12,
        },
      },
      {
        fieldName: null,
        text: (
          <h2 className="case-step-title">
            <TranslatedText i18nKey="cases.request_info.titles.title3" />
          </h2>
        ),
        grid: {
          xs: 12,
        },
      },

      {
        fieldName: note.name,
        grid: {
          xs: 12,
        },
      },
    ],
    content: (
      <div>
        <p>
          <TranslatedText i18nKey="cases.info.legal1" />
        </p>
        <p>
          <TranslatedText i18nKey="cases.info.legal2" />
        </p>
      </div>
    ),
  },
];

const getFormDetails = () => {
  const pdfLink = getCorrectLink(pdfModuloSepa);
  return {
    title: <TranslatedText i18nKey="cases.domiciliazione_bancaria.title" />,
    description: null,
    summary: false,
    stepper: CaseStepperType.None,
    autoOpenHelpModal: false,
    backTo:'/',
    backToText: <TranslatedText i18nKey="common.back.home" />,
    blockIfNoContracts: true,
    belowText: (
      <>
        <p>
          <TranslatedText i18nKey="cases.domiciliazione_bancaria.below_text.first_paragraph" />
          &nbsp;
          <a
            href={pdfLink}
            download>
            {" "}
            <TranslatedText i18nKey="cases.domiciliazione_bancaria.below_text.first_paragraph_link" />
            :
          </a>
        </p>
        <ul>
          <li>
            <TranslatedText i18nKey="cases.domiciliazione_bancaria.below_text.email" />&nbsp;
            <a href="mailto:adesionisepa@gruppocap.it">
              adesionisepa@gruppocap.it
            </a>
          </li>
          <li>
            <TranslatedText i18nKey="cases.domiciliazione_bancaria.below_text.fax" />
          </li>
          <li>
            <TranslatedText i18nKey="cases.domiciliazione_bancaria.below_text.address" />
          </li>
        </ul>
        <p>
            <TranslatedText i18nKey="cases.domiciliazione_bancaria.below_text.last_paragraph" />
        </p>
      </>
    ),
    blockText: (
      <div className="content">
        <p>
          <TranslatedText i18nKey="cases.modal.contracts.block_text" />
        </p>
      </div>
    ),
    successText: (
      <div>
        <p>
          <TranslatedText i18nKey="cases.request_info.success_text" />
        </p>
      </div>
    ),
  }
}

const getTrackingEvent = () => () => { window.dataLayer.push({ "event": "domiciliazioneBancariaCompletata" }) }

// Contiene dettagli sul case form

const mutation = {
  type: CaseMutationType.Simple,
};

const main = () => {
  return {
    model,
    validationSchema: getValidationSchema(),
    initialValues,
    layout,
    formDetails: getFormDetails(),
    mutation,
    trackingEvent: getTrackingEvent(),
  };
};

export default main;
